import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';

const RecuperarPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/password-reset/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setEmailSent(true);
        toast({
          title: '✅ Email enviado',
          description: 'Si el correo existe en nuestro sistema, recibirás instrucciones para recuperar tu contraseña.',
        });
      } else {
        toast({
          title: 'Error',
          description: data.detail || 'Hubo un problema al enviar el email. Intenta de nuevo.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error al solicitar recuperación:', error);
      toast({
        title: 'Error de conexión',
        description: 'No se pudo conectar con el servidor. Intenta de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 py-12 px-4">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_morabio-web/artifacts/o1ie7x5d_LogoMoraBioNegro.jpg" 
            alt="MoraBioVet" 
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-[#1e3a8a] mb-2">Recuperar contraseña</h1>
          <p className="text-gray-600">Te enviaremos instrucciones por correo</p>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-center text-[#1e3a8a]">
              {emailSent ? '📧 Revisa tu correo' : '🔒 Restablecer contraseña'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!emailSent ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                  <p className="text-sm text-gray-700">
                    Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Correo electrónico
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <Input
                      type="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#1e40af] hover:bg-[#1e3a8a] text-white py-6 text-lg"
                  disabled={isLoading}
                >
                  {isLoading ? 'Enviando...' : 'Enviar instrucciones'}
                </Button>

                <div className="text-center">
                  <Link 
                    to="/login" 
                    className="text-[#1e40af] hover:text-[#1e3a8a] text-sm flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Volver al inicio de sesión
                  </Link>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-4">
                  <p className="text-sm text-gray-700">
                    Si tu correo <strong>{email}</strong> está registrado en nuestro sistema, 
                    recibirás un email con instrucciones para restablecer tu contraseña en los próximos minutos.
                  </p>
                </div>

                <div className="space-y-3 text-sm text-gray-600">
                  <p>📩 Revisa tu bandeja de entrada y también la carpeta de spam.</p>
                  <p>⏱️ El enlace de recuperación expirará en 24 horas.</p>
                </div>

                <div className="pt-4 space-y-3">
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
                  >
                    Volver al inicio de sesión
                  </Button>
                  
                  <button
                    onClick={() => {
                      setEmailSent(false);
                      setEmail('');
                    }}
                    className="w-full text-[#1e40af] hover:text-[#1e3a8a] text-sm"
                  >
                    ¿No recibiste el email? Reenviar
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-gray-700">
            <p className="font-semibold mb-2">¿Necesitas ayuda?</p>
            <p>
              Contacta a nuestro soporte al <strong>5545547772</strong> o 
              envía un correo a <strong>info@morabiovet.com</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecuperarPassword;
